import { useEffect, useMemo } from 'react'
import { useAppSelector } from './useAppSelector'
import { useBindDispatch } from './useBindDispatch'
import {
    ClientDetailsPayloadInterface,
    InteractionPayloadType,
    TargetPayloadType
} from 'storeTypes'
import { constants } from 'helpers'
import { compact } from 'lodash'

export const useProcessTargetList = (veData: {
    veCode: string
    villageId: string
}) => {
    const villageClientsStore = useAppSelector(
        (state) => state.villageClientList
    )
    const villagePotentialClientsStore = useAppSelector(
        (state) => state.villagePreviousClientList
    )
    const { villageId, veCode } = veData

    const [clientList, potentialClients] = useMemo(() => {
        const clientList = Object.values(villageClientsStore).flatMap(
            (list) => list.clientList
        )
        const potentialClients = Object.values(
            villagePotentialClientsStore
        ).flatMap((list) => list.previousClientList)

        return [clientList, potentialClients]
    }, [villageClientsStore])

    const { targetList, processedTargetList, selectedFilter } = useAppSelector(
        (state) => state.targetList[veCode] || { targetList: [] }
    )
    const { interactions: veInteractions } = useAppSelector(
        (state) => state.veInteractions
    )
    const { interactions } = useAppSelector(
        (state) => state.interactions?.[villageId]
    ) || { interactions: [] }

    const {
        processTargetListHandler,
        getInteractionsHandler,
        fetchTargetListHandler
    } = useBindDispatch()

    useEffect(() => {
        if (targetList.length === 0) {
            fetchTargetListHandler({ veCode })
        }
    }, [])

    const clients = useMemo(() => {
        return [
            ...Object.values(clientList || []),
            ...Object.values(potentialClients || [])
        ].flat() as unknown as ClientDetailsPayloadInterface[]
    }, [clientList, potentialClients])

    const isExpired = useMemo(() => {
        const now = new Date()

        return (processedTargetList || []).some((target) =>
            target.targeting_end_date
                ? new Date(target.targeting_end_date) < now
                : false
        )
    }, [processedTargetList])

    useEffect(() => {
        if (clients.length > 0 && targetList.length > 0) {
            const clientCodes = compact(
                targetList.map(
                    (target: TargetPayloadType) => target.client_code
                )
            )

            processTargetListHandler({
                targetList,
                clients,
                interactions: (
                    veInteractions as InteractionPayloadType[]
                ).filter(
                    (interaction) =>
                        clientCodes.includes(interaction.client_code) &&
                        interaction.survey_version ===
                            constants.TARGET_LIST_SURVEY_VERSION
                ),
                selectedFilter,
                veCode
            })
        }
    }, [veInteractions, targetList, clients])

    useEffect(() => {
        if (targetList?.length > 0 && (interactions || []).length > 0) {
            const clientCodes = compact(
                targetList.map(
                    (target: TargetPayloadType) => target.client_code
                )
            )

            getInteractionsHandler({
                clientCodes,
                surveyName: constants.TARGET_LIST_SURVEY_NAME,
                surveyVersion: constants.TARGET_LIST_SURVEY_VERSION
            })
        }
    }, [targetList, interactions])

    return {
        isExpired,
        targetList,
        processedTargetList,
        storeSelectedFilter: selectedFilter
    }
}
